import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Badge, Button, Card, EmptyState, Inline, Input, Select, Stack, Text } from '../components/atoms';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { ApiError, apiClient } from '../lib/apiClient';
import styles from './AdminAuditLogPage.module.css';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';

/** One row of GET /api/v1/admin/audit-logs (admin-only, newest first). */
export interface AuditLogEntry {
  id: string;
  action: string;
  actorId: string | null;
  actorEmail: string | null;
  targetType: string | null;
  targetId: string | null;
  ipAddress: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

interface AuditLogPage {
  entries: AuditLogEntry[];
  total: number;
  page: number;
  limit: number;
  actions: string[];
}

interface Filters {
  action: string;
  from: string;
  to: string;
}

const PAGE_SIZE = 25;
const NO_FILTERS: Filters = { action: '', from: '', to: '' };

async function fetchAuditLogs(page: number, filters: Filters): Promise<AuditLogPage> {
  const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
  if (filters.action) params.set('action', filters.action);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  const response = await apiClient.get<{ data: AuditLogPage }>(`/api/v1/admin/audit-logs?${params.toString()}`);
  return response.data;
}

function AuditLogItem({ entry }: { entry: AuditLogEntry }) {
  const { t } = useTranslation();
  const metadata = Object.keys(entry.metadata ?? {}).length > 0 ? JSON.stringify(entry.metadata) : null;
  const target = [entry.targetType, entry.targetId].filter(Boolean).join(' · ');

  return (
    <li>
      <Card padding="md" className={styles.entry}>
        <Stack gap="2">
          <Inline gap="2" wrap align="center" justify="between">
            <Badge tone="info">{entry.action}</Badge>
            <Text variant="caption" className={styles.muted}>
              <time dateTime={entry.createdAt}>{new Date(entry.createdAt).toLocaleString()}</time>
            </Text>
          </Inline>
          <dl className={styles.details}>
            <div className={styles.detail}>
              <dt className={styles.term}>{t('admin.auditLog.actor')}</dt>
              <dd className={styles.value}>{entry.actorEmail ?? entry.actorId ?? t('admin.auditLog.system')}</dd>
            </div>
            {target && (
              <div className={styles.detail}>
                <dt className={styles.term}>{t('admin.auditLog.target')}</dt>
                <dd className={styles.value}>{target}</dd>
              </div>
            )}
            {entry.ipAddress && (
              <div className={styles.detail}>
                <dt className={styles.term}>{t('admin.auditLog.ipAddress')}</dt>
                <dd className={styles.value}>{entry.ipAddress}</dd>
              </div>
            )}
            {metadata && (
              <div className={styles.detail}>
                <dt className={styles.term}>{t('admin.auditLog.details')}</dt>
                <dd className={styles.value}><code className={styles.code}>{metadata}</code></dd>
              </div>
            )}
          </dl>
        </Stack>
      </Card>
    </li>
  );
}

export function AdminAuditLogPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'audit-logs', page, filters],
    queryFn: () => fetchAuditLogs(page, filters),
    placeholderData: keepPreviousData,
  });

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = filters.action !== '' || filters.from !== '' || filters.to !== '';
  const forbidden = error instanceof ApiError && error.status === 403;
  const actionOptions = [
    { value: '', label: t('admin.auditLog.allActions') },
    ...(data?.actions ?? []).map((action) => ({ value: action, label: action })),
  ];

  function updateFilter(key: keyof Filters, value: string): void {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function resetFilters(): void {
    setFilters(NO_FILTERS);
    setPage(1);
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent>
          <PageHeader title={t('admin.auditLog.title')} description={t('admin.auditLog.subtitle')} />

          <Section title={t('admin.auditLog.filters')}>
            <Card padding="md">
              <div className={styles.filters}>
                <Select
                  label={t('admin.auditLog.action')}
                  options={actionOptions}
                  value={filters.action}
                  onChange={(e) => updateFilter('action', e.target.value)}
                  fullWidth
                />
                <Input
                  type="date"
                  label={t('admin.auditLog.from')}
                  value={filters.from}
                  max={filters.to || undefined}
                  onChange={(e) => updateFilter('from', e.target.value)}
                  fullWidth
                />
                <Input
                  type="date"
                  label={t('admin.auditLog.to')}
                  value={filters.to}
                  min={filters.from || undefined}
                  onChange={(e) => updateFilter('to', e.target.value)}
                  fullWidth
                />
              </div>
              {hasFilters && (
                <div className={styles.resetRow}>
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    {t('admin.auditLog.resetFilters')}
                  </Button>
                </div>
              )}
            </Card>
          </Section>

          <Section title={t('admin.auditLog.entries', { count: total })}>
            {isLoading && (
              <LiveRegion>
                <Text>{t('admin.auditLog.loading')}</Text>
              </LiveRegion>
            )}

            {isError && (
              <LoadErrorState
                title={forbidden ? t('admin.auditLog.forbidden') : t('admin.auditLog.error')}
                onRetry={forbidden ? undefined : () => void refetch()}
                retryLabel={t('admin.auditLog.retry')}
              />
            )}

            {!isLoading && !isError && entries.length === 0 && (
              <EmptyState
                icon={<ScrollText size={48} strokeWidth={1.5} />}
                headline={hasFilters ? t('admin.auditLog.emptyFilterTitle') : t('admin.auditLog.emptyTitle')}
                description={hasFilters ? t('admin.auditLog.emptyFilterMessage') : t('admin.auditLog.emptyMessage')}
                actionLabel={hasFilters ? t('admin.auditLog.resetFilters') : t('admin.auditLog.reload')}
                onAction={hasFilters ? resetFilters : () => void refetch()}
              />
            )}

            {!isLoading && !isError && entries.length > 0 && (
              <Stack gap="4">
                <ol className={styles.list}>
                  {entries.map((entry) => (
                    <AuditLogItem key={entry.id} entry={entry} />
                  ))}
                </ol>
                <nav aria-label={t('admin.auditLog.pagination')}>
                  <Inline gap="3" align="center" justify="between" wrap>
                    <Button variant="secondary" size="sm" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
                      {t('admin.auditLog.previous')}
                    </Button>
                    <Text variant="caption" aria-live="polite">
                      {t('admin.auditLog.pageOf', { page, pages: pageCount })}
                    </Text>
                    <Button variant="secondary" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= pageCount}>
                      {t('admin.auditLog.next')}
                    </Button>
                  </Inline>
                </nav>
              </Stack>
            )}
          </Section>
        </PageContent>
      }
    />
  );
}

export default AdminAuditLogPage;
